import React from 'react';
import bowser from 'yola-bowser';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { utils } from '@yola/ws-sdk';
import { Modal, Panel, PanelGroup, designSystem } from '@yola/ws-ui';

const {
  Heading,
  Paragraph,
  Box,
  TreeView,
  FloatingAction,
  DialogHeader,
  DialogHeaderTitle,
  DialogButtonGroup,
  DialogButton,
  DialogButtonGroupSeparator,
} = designSystem;

const defaultItems = [];

const LinkListSettingsDialog = ({
  modalRef,
  items = defaultItems,
  onAddNewItem,
  onClose = utils.noop,
  captions,
  interaction,
  maxNestingLevel,
  offsetX,
  offsetY,
  dialogWidth,
  dialogHeight,
  handleKeyDown = utils.noop,
  applyChanges = utils.noop,
  modalContentClassName,
  onOrderChange = utils.noop,
}) => {
  const isEmpty = items.length === 0;
  const contentClassName = classNames(
    'ws-link-list-settings__panel',
    'ws-modal__content',
    'ws-panel--scrollable',
    {
      modalContentClassName,
    }
  );

  return (
    <Modal
      modalRef={modalRef}
      resizable
      width={dialogWidth}
      height={dialogHeight}
      minWidth={320}
      minHeight={200}
      offsetX={offsetX}
      offsetY={offsetY}
      handleCancel={onClose}
      handleSubmit={applyChanges}
      dragHandleSelector=".ws-drag-trigger"
      onKeyDown={handleKeyDown}
      fullscreen={bowser.mobile}
      className="ws-link-list-settings"
    >
      <PanelGroup height="100%">
        <Panel
          theme="gray-100"
          corners="squared"
          align="middle"
          style={{ flexShrink: '0', paddingLeft: 0 }}
          height={48}
          className="ws-drag-trigger ws-modal__content"
        >
          <DialogHeader>
            <DialogHeaderTitle>{captions.dialogTitle}</DialogHeaderTitle>
          </DialogHeader>
        </Panel>
        <Panel
          theme="white"
          className={contentClassName}
          height="100%"
          {...(isEmpty && { align: 'middle' })}
        >
          {isEmpty ? (
            <div className="ws-link-list-settings__empty-state">
              <Heading type="heading-2" appearance="low-emphasis">
                {captions.emptyTitle}
              </Heading>
              <Paragraph size="large" appearance="low-emphasis">
                {captions.emptyDescription}
              </Paragraph>
            </div>
          ) : (
            <Box paddingTop="spacing-xs" paddingBottom="spacing-xl" marginBottom="spacing-xl">
              <TreeView
                items={items}
                onChange={onOrderChange}
                interaction={interaction}
                maxNestingLevel={maxNestingLevel}
              />
            </Box>
          )}
        </Panel>
        <Panel theme="white" style={{ flexShrink: '0' }} height={48}>
          <DialogButtonGroup>
            <DialogButton disabled={isEmpty} onClick={applyChanges}>
              {captions.submit}
            </DialogButton>
            <DialogButtonGroupSeparator />
            <DialogButton onClick={onClose}>{captions.cancel}</DialogButton>
          </DialogButtonGroup>
        </Panel>
      </PanelGroup>
      <div className="ws-link-list-settings__floating-action-wrapper">
        <FloatingAction onClick={onAddNewItem} />
      </div>
    </Modal>
  );
};

LinkListSettingsDialog.propTypes = {
  modalRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })])
    .isRequired,
  offsetX: PropTypes.number.isRequired,
  offsetY: PropTypes.number.isRequired,
  modalContentClassName: PropTypes.string.isRequired,
  items: TreeView.propTypes.items,
  applyChanges: PropTypes.func,
  onClose: PropTypes.func,
  onAddNewItem: PropTypes.func.isRequired,
  dialogHeight: PropTypes.number.isRequired,
  dialogWidth: PropTypes.number.isRequired,
  interaction: PropTypes.string.isRequired,
  maxNestingLevel: PropTypes.number.isRequired,
  captions: PropTypes.shape({
    dialogTitle: PropTypes.string,
    emptyTitle: PropTypes.string,
    emptyDescription: PropTypes.string,
    submit: PropTypes.string,
    cancel: PropTypes.string,
  }).isRequired,
  handleKeyDown: PropTypes.func,
  onOrderChange: PropTypes.func,
};

export default LinkListSettingsDialog;
